import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIonToast } from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import SecondaryButton from '@components/buttons/SecondaryButton';
import BaseErrors from '@components/fields/BaseErrors';
import DateInput from '@components/fields/DateInput';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RequiredFieldIndicator from '@components/fields/RequiredFieldIndicator';
import Select from '@components/fields/Select';
import ToggleCheckbox from '@components/fields/ToggleCheckbox';
import Form from '@components/forms/Form';
import styles from '@components/forms/TimeLogForm.module.css';
import HandleQuery from '@components/HandleQuery';
import InputLabel from '@components/typography/InputLabel';
import useMyExpert from '@hooks/useMyExpert';
import { thirtyDaysInMs } from '@typing/Constants';
import {
  GetTimeLogActivityKindsDocument,
  GetTimeLogActivityKindsQuery,
  GetTimeLogActivityKindsQueryVariables,
  GetTimeLogSupportKindsDocument,
  GetTimeLogSupportKindsQuery,
  GetTimeLogSupportKindsQueryVariables,
  TimeLogFragment as TimeLog,
  TimeLogCreateDocument,
  TimeLogCreateMutation,
  TimeLogCreateMutationVariables,
  TimeLogFragmentDoc,
  TimeLogSupportCategoryEnum,
  TimeLogUpdateDocument,
  TimeLogUpdateMutation,
  TimeLogUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { IconList } from '@utils/iconUtils';
import { journeyRoute } from '@utils/routeUtils';
import { dateToMDYString } from '@utils/timeUtils';

type FormData = {
  activityKindId: string;
  date: string;
  journeyId: string;
  minutes: number | null;
  notes: string;
  supportCategory: string;
  supportKindId: string;
};

export type CreateCache = ApolloCache<TimeLogCreateMutation>;

export type Journey = {
  carePlanPublishedAt: Date | null;
  hasAvailableCarePlanningPackage: boolean;
  id: string;
};

type Props = {
  autofocus: boolean;
  journey?: Journey;
  onSuccess?: () => void;
  onUpdate?: () => void;
  oneColumn?: boolean;
  refetch?: () => void;
  timeLog?: TimeLog;
};

const TimeLogForm = ({
  autofocus,
  journey,
  onSuccess,
  onUpdate,
  oneColumn = false,
  refetch,
  timeLog = undefined
}: Props) => {
  const myExpert = useMyExpert();

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [present, dismiss] = useIonToast();

  const onTimeLogSuccess = useCallback(() => {
    present({
      buttons: [{ handler: () => dismiss(), icon: IconList.check }],
      color: 'success',
      cssClass: 'toast',
      duration: 5000,
      message: intl.formatMessage({ id: 'pages.carePlan.base.expert.timeLogSubmitted' }),
      position: 'top'
    });
  }, [dismiss, intl, present]);

  const [isCarePlanning, setIsCarePlanning] = useState<boolean | undefined>();

  const [hideIsCarePlanning, setHideIsCarePlanning] = useState(false);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      activityKindId: yup.string().required(intl.formatMessage({ id: 'errors.timeLog.activityKindId.blank' })),
      date: yup.string().required(intl.formatMessage({ id: 'errors.timeLog.date.blank' })),
      journeyId: yup.string().defined(),
      minutes: yup
        .number()
        .required(intl.formatMessage({ id: 'errors.timeLog.minutes.blank' }))
        .typeError(intl.formatMessage({ id: 'errors.timeLog.minutes.number' }))
        .min(0, intl.formatMessage({ id: 'errors.timeLog.minutes.positive' }))
        .max(99, intl.formatMessage({ id: 'errors.timeLog.minutes.tooLarge' })),
      notes: yup.string().defined(),
      supportCategory: yup.string().required(intl.formatMessage({ id: 'errors.timeLog.supportCategory.blank' })),
      supportKindId: yup.string().required(intl.formatMessage({ id: 'errors.timeLog.supportKindId.blank' }))
    })
    .required();

  let defaultDate = '';
  if (timeLog?.date) {
    defaultDate = dateToMDYString(timeLog.date);
  }

  const defaultValues = {
    activityKindId: timeLog?.activityKind.id ?? '',
    date: defaultDate,
    journeyId: journey?.id ?? timeLog?.journey?.id ?? '',
    minutes: timeLog?.minutes ? timeLog.minutes : null,
    notes: timeLog?.notes ?? '',
    supportCategory: timeLog?.supportKind?.category ?? '',
    supportKindId: timeLog?.supportKind?.id ?? ''
  };

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const watchJourneyId = form.watch('journeyId', defaultValues.journeyId);
  const watchActivityKindId = form.watch('activityKindId');
  const watchSupportCategory = form.watch('supportCategory', defaultValues.supportCategory);

  const getActivityKindsQuery = useQuery<GetTimeLogActivityKindsQuery, GetTimeLogActivityKindsQueryVariables>(
    GetTimeLogActivityKindsDocument
  );
  const getSupportKindsQuery = useQuery<GetTimeLogSupportKindsQuery, GetTimeLogSupportKindsQueryVariables>(
    GetTimeLogSupportKindsDocument
  );

  useEffect(() => {
    if (journey?.hasAvailableCarePlanningPackage && !timeLog) {
      const activityKind = getActivityKindsQuery.data?.timeLogActivityKinds.find(
        kind => kind.id === watchActivityKindId
      );
      if (activityKind?.carePlanningSpecific) {
        setIsCarePlanning(true);
      }
    }
  }, [
    journey?.hasAvailableCarePlanningPackage,
    watchActivityKindId,
    timeLog,
    getActivityKindsQuery.data?.timeLogActivityKinds
  ]);

  const activityKindOptions = useMemo(() => {
    const options = getActivityKindsQuery.data?.timeLogActivityKinds.filter(activityKind => !activityKind.legacy);

    if (options) {
      return [
        {
          label: intl.formatMessage({ id: 'models.timeLogActivityKinds.billable' }),
          options: options
            ?.filter(option => option.billable)
            .map(kind => ({
              description: intl.formatMessage({ id: `models.timeLogActivityKinds.${kind.kind}.description` }),
              label: intl.formatMessage({ id: `models.timeLogActivityKinds.${kind.kind}.title` }),
              selectedDescription: intl.formatMessage({ id: 'models.timeLogActivityKinds.billableOption' }),
              value: kind.id
            }))
        },
        {
          label: intl.formatMessage({ id: 'models.timeLogActivityKinds.nonBillable' }),
          options: options
            ?.filter(option => !option.billable)
            .map(kind => ({
              description: intl.formatMessage({ id: `models.timeLogActivityKinds.${kind.kind}.description` }),
              label: intl.formatMessage({ id: `models.timeLogActivityKinds.${kind.kind}.title` }),
              selectedDescription: intl.formatMessage({ id: 'models.timeLogActivityKinds.nonBillableOption' }),
              value: kind.id
            }))
        }
      ];
    }
    return [];
  }, [getActivityKindsQuery.data?.timeLogActivityKinds, intl]);

  const supportCategoryOptions = useMemo(
    () =>
      Object.values(TimeLogSupportCategoryEnum).map(option => ({
        label: intl.formatMessage({ id: `models.timeLogSupportCategories.${option}` }),
        value: option
      })),
    [intl]
  );

  const supportKindOptions = useMemo(
    () =>
      getSupportKindsQuery.data?.timeLogSupportKinds
        .filter(kind => kind.category === watchSupportCategory)
        .map(kind => ({
          label: intl.formatMessage({ id: `models.timeLogSupportKinds.${kind.kind}` }),
          value: kind.id
        })),
    [getSupportKindsQuery.data?.timeLogSupportKinds, intl, watchSupportCategory]
  );

  useEffect(() => {
    if (watchSupportCategory !== timeLog?.supportKind?.category) {
      form.setValue('supportKindId', '');
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [watchSupportCategory]);

  useEffect(() => {
    if (!watchJourneyId) {
      form.setValue('supportCategory', '');
      form.setValue('supportKindId', '');
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [watchJourneyId]);

  useEffect(() => {
    const currentJourney = journey;
    if (!currentJourney) {
      setHideIsCarePlanning(true);
    } else {
      if (timeLog) {
        setHideIsCarePlanning(!currentJourney.hasAvailableCarePlanningPackage);
        setIsCarePlanning(timeLog.isCarePlanning);
      } else {
        if (currentJourney.hasAvailableCarePlanningPackage) {
          if (!currentJourney.carePlanPublishedAt) {
            setHideIsCarePlanning(false);
            setIsCarePlanning(undefined);
          } else {
            if (new Date(currentJourney.carePlanPublishedAt.getTime() + thirtyDaysInMs) < new Date()) {
              setHideIsCarePlanning(true);
              setIsCarePlanning(false);
            } else {
              setHideIsCarePlanning(false);
              setIsCarePlanning(false);
            }
          }
        } else {
          setHideIsCarePlanning(true);
          setIsCarePlanning(false);
        }
      }
    }
  }, [journey, timeLog, watchJourneyId]);

  const generateHandlePackageSelect = useCallback(
    (value: boolean) => () => {
      if (value === isCarePlanning) {
        setIsCarePlanning(undefined);
      } else {
        setIsCarePlanning(value);
      }
    },
    [isCarePlanning]
  );

  const [timeLogCreate] = useMutation<TimeLogCreateMutation, TimeLogCreateMutationVariables>(TimeLogCreateDocument, {
    update: (cache, { data }) => {
      cache.modify({
        fields: {
          timeLogs(cachedTimeLogRefs: readonly Reference[]) {
            if (data?.timeLogCreate?.timeLog) {
              const timeLogRef = cache.writeFragment({
                data: data.timeLogCreate.timeLog,
                fragment: TimeLogFragmentDoc,
                fragmentName: 'TimeLog'
              });

              if (timeLogRef) {
                return [...cachedTimeLogRefs, timeLogRef];
              }
            }
            return [...cachedTimeLogRefs];
          }
        },
        id: cache.identify({ ...data?.timeLogCreate?.timeLog?.journey })
      });
      if (data?.timeLogCreate?.timeLog?.expert) {
        cache.modify({
          fields: {
            timeLogs(cachedTimeLogRefs: readonly Reference[]) {
              if (data.timeLogCreate?.timeLog) {
                const timeLogRef = cache.writeFragment({
                  data: data.timeLogCreate.timeLog,
                  fragment: TimeLogFragmentDoc,
                  fragmentName: 'TimeLog'
                });

                if (timeLogRef) {
                  return [...cachedTimeLogRefs, timeLogRef];
                }
              }
              return [...cachedTimeLogRefs];
            }
          },
          id: cache.identify({ ...data.timeLogCreate.timeLog.expert })
        });
      }
      refetch?.();
    }
  });

  const [timeLogUpdate] = useMutation<TimeLogUpdateMutation, TimeLogUpdateMutationVariables>(TimeLogUpdateDocument);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    const minutes = form.getValues().minutes;
    if (minutes === null) {
      return;
    }
    if (timeLog) {
      return timeLogUpdate({
        variables: {
          ...form.getValues(),
          id: timeLog.id,
          isCarePlanning: isCarePlanning ?? false,
          minutes: parseInt(minutes.toString())
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.timeLogUpdate,
          intl,
          onSuccess: () => {
            if (onUpdate && response.data?.timeLogUpdate?.timeLog) {
              onUpdate();
              refetch?.();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.timeLog'
        });
      });
    }
    return timeLogCreate({
      variables: {
        ...form.getValues(),
        isCarePlanning: isCarePlanning ?? false,
        journeyId: journey?.id ?? form.getValues().journeyId,
        minutes: parseInt(minutes.toString())
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.timeLogCreate,
        intl,
        onSuccess: () => {
          if (response.data?.timeLogCreate?.timeLog) {
            form.reset();
            onTimeLogSuccess();
            if (onSuccess) {
              onSuccess();
            }
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.timeLog'
      });
    });
  }, [
    form,
    timeLog,
    timeLogCreate,
    isCarePlanning,
    journey?.id,
    timeLogUpdate,
    intl,
    onUpdate,
    refetch,
    onTimeLogSuccess,
    onSuccess
  ]);

  return (
    <HandleQuery query={getActivityKindsQuery}>
      {_data => (
        <div className={styles.wrapperItem}>
          {supportKindOptions && supportCategoryOptions && activityKindOptions && (
            <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
              <BaseErrors errors={baseErrors} />
              <div className={oneColumn ? styles.oneColumn : styles.oneRow}>
                <DateInput<FormData>
                  autofocus={autofocus}
                  control={form.control}
                  error={form.formState.errors.date}
                  inputStyles
                  label={intl.formatMessage({ id: 'forms.timeLog.dateLabel' })}
                  name="date"
                  required
                />
                <div>
                  <Input<FormData>
                    control={form.control}
                    error={form.formState.errors.minutes}
                    label={intl.formatMessage({ id: 'forms.timeLog.minutesLabel' })}
                    name="minutes"
                    required
                    type="number"
                  />
                </div>

                <div className={hideIsCarePlanning ? 'ion-hide' : undefined}>
                  <div>
                    <InputLabel>
                      <FormattedMessage id="forms.timeLog.carePlanPackageLabel" />
                    </InputLabel>
                    <RequiredFieldIndicator />
                  </div>
                  <div className={styles.packageToggles}>
                    <ToggleCheckbox
                      checked={isCarePlanning === false}
                      labelKey="dictionary.no"
                      name="marketingEmails"
                      onClick={generateHandlePackageSelect(false)}
                      tabIndex={0}
                    />
                    <ToggleCheckbox
                      checked={isCarePlanning === true}
                      labelKey="dictionary.yes"
                      name="marketingEmails"
                      onClick={generateHandlePackageSelect(true)}
                      tabIndex={0}
                    />
                  </div>
                </div>
                <div>
                  <Select<FormData>
                    control={form.control}
                    error={form.formState.errors.activityKindId}
                    label={intl.formatMessage({ id: 'forms.timeLog.activityKindIdLabel' })}
                    name="activityKindId"
                    options={activityKindOptions}
                    placeholder=""
                    required
                  />
                </div>
                <div>
                  <Select<FormData>
                    control={form.control}
                    disabled={!form.getValues().journeyId}
                    error={form.formState.errors.supportCategory}
                    label={intl.formatMessage({ id: 'forms.timeLog.supportCategoryLabel' })}
                    name="supportCategory"
                    options={supportCategoryOptions}
                    placeholder=""
                    required
                  />
                </div>

                <div>
                  <Select<FormData>
                    control={form.control}
                    disabled={!form.getValues().journeyId || supportCategoryOptions.length === 0}
                    error={form.formState.errors.supportKindId}
                    label={intl.formatMessage({ id: 'forms.timeLog.supportKindIdLabel' })}
                    name="supportKindId"
                    options={supportKindOptions}
                    placeholder=""
                    required
                  />
                </div>
              </div>
              <div className={styles.notes}>
                <Input<FormData>
                  control={form.control}
                  error={form.formState.errors.notes}
                  label={intl.formatMessage({ id: 'forms.timeLog.notesLabel' })}
                  name="notes"
                  spellcheck
                />
              </div>

              <div className={styles.buttons}>
                <FormButtons
                  buttonSize="large"
                  expand="block"
                  hookForm={form}
                  saveDisabled={isCarePlanning === undefined}
                  saveKey="dictionary.save"
                  skipCancel
                />
                {oneColumn && (
                  <SecondaryButton
                    expand="block"
                    routerLink={journeyRoute({
                      journeyId: journey?.id ?? '',
                      linkAfterJourney: '/time-logs',
                      myExpert
                    })}
                    size="large"
                    textKey="pages.carePlan.base.expert.timeLogHistory"
                  />
                )}
              </div>
            </Form>
          )}
        </div>
      )}
    </HandleQuery>
  );
};

export default TimeLogForm;
